/**
 * UpSurv 2.0 — what the office will accept as a photo, and how to tell.
 *
 * ══ THE 2026-08-07 FIELD DEFECT ══════════════════════════════════════════════
 *
 * The design authority, in her own words:
 *
 *   "When I take a picture, it says the photo is on my phone and the office
 *    cannot accept it. I need to retake it, resend it, or delete it and take it
 *    again. I've done that, and it still won't accept it. It accepts photos from
 *    the library without a problem, but a live shot won't be accepted."
 *
 * The asymmetry was the whole clue, and it was not in the metadata the form
 * builds — that is identical on both paths — it was in the `File` the browser
 * handed over. A live capture on iOS can arrive with `file.type === ''`. A photo
 * chosen from the library never does, because the Photos app knows the UTI.
 *
 * An empty-typed Blob is not neutral once it reaches `FormData`. The
 * multipart/form-data serializer is required to stamp a part with SOME content
 * type, and for a Blob with no type that value is `application/octet-stream`.
 * So the Worker saw:
 *
 *     const contentType = (file.type || meta.contentType || '').toLowerCase();
 *
 * …with `file.type` = `"application/octet-stream"` — TRUTHY. The `meta.contentType`
 * fallback, which is exactly the field that carried the truth, was unreachable
 * dead code. `extensionFor("application/octet-stream")` returned null and the
 * upload was refused **415**, forever, on every retake, because every retake
 * produced another empty-typed file.
 *
 * ══ What this module is ══════════════════════════════════════════════════════
 *
 * ONE registry of the types the office accepts, shared by the capture screen,
 * the transport and the Worker, so the three cannot drift apart the way
 * `PhotoRefSchema` and `PhotoMetaSchema` once did — and one honest way to work
 * out what a file actually IS when the operating system declines to say.
 *
 * The order of that decision is deliberate:
 *
 *   1. what the OS declared, if the office accepts it — it is the only party
 *      that has seen the original asset and its UTI;
 *   2. the file's own magic bytes — a JPEG says so in its first three bytes and
 *      cannot be talked out of it;
 *   3. the filename's extension — last, because a name is the easiest thing on
 *      a file to be wrong;
 *   4. nothing. Refuse, AT CAPTURE, while the surveyor is still standing in
 *      front of the thing they photographed.
 *
 * Nothing here re-encodes, rotates or strips anything: EXIF orientation
 * arrives at R2 exactly as the camera wrote it.
 *
 * ══ THE 2026-08-19 FIELD DEFECT — the upload that left the phone EMPTY ═════════
 *
 * Caught live in `wrangler pages deployment tail` while the design authority
 * retook two photos on her iPhone (iOS 18.7, the app installed on the home
 * screen): every `POST /api/photos` arrived with
 *
 *     content-type: multipart/form-data; boundary=----WebKitFormBoundary…
 *     content-length: 0
 *
 * A multipart header and NOT ONE BYTE of body. The Worker's `formData()`
 * threw, the office answered 400, and — on the build before D-025 — the device
 * parked the photo after one attempt under a sentence that blamed the picture.
 * That is the whole of "the pictures won't sync", from her five on 2026-08-10
 * to Chip's "most of them" on 2026-08-18: the phone was never sending the bytes.
 *
 * What changed on 2026-08-07 was this module's own fix for the live-camera 415:
 * `typedPhotoBlob` re-typed the Blob with `Blob.slice(0, size, type)` — a LAZY
 * VIEW, "no bytes are copied" — at capture (what went into IndexedDB) and again
 * at upload (what went into the multipart part). In Node and Chromium a slice of
 * a file-backed Blob serialises like any other; on iOS WebKit, from an installed
 * web app, the FormData serialiser could not read it and sent the part empty.
 * The unit suite drives the real Worker and the E2E suite drives real Chromium,
 * and neither is an iPhone — which is why this was green for twelve days while
 * every field photo failed.
 *
 * So the device no longer hands the browser a Blob HANDLE anywhere on the
 * photo's path. At capture the File is READ into memory (`readPhotoBytes`) and
 * what is stored is a Blob built from those bytes; at upload the stored Blob is
 * read again into a `Uint8Array` and the request body IS those bytes — raw, the
 * content type in the header, the metadata in `x-photo-meta` — with no
 * multipart and no lazy view for any browser to mis-serialise. A read that
 * comes back empty is refused at capture ("the phone could not read that
 * picture") or parked at upload ("the picture file on this device is empty"),
 * out loud, instead of leaving over the wire as nothing.
 */

/* ═════════════════════════ the registry (one copy) ═════════════════════════ */

/**
 * Content type → the extension the R2 key gets. The office's whole answer to
 * "can you open this", in one object, imported by:
 *   · functions/api/[[route]].ts   — the refusal
 *   · PhotoCapture                 — the capture-time guard
 * When those two disagreed about a field, one photo was refused forty-two times
 * (see photo-device-path.test.ts). They no longer can.
 */
export const ACCEPTED_PHOTO_TYPES: Readonly<Record<string, string>> = Object.freeze({
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/avif': 'avif',
});

/** Photos are field snapshots, not scans. Bounds the Worker's memory too. */
export const MAX_PHOTO_BYTES = 25 * 1024 * 1024;

/**
 * The values that mean "nobody told me what this is".
 *
 * `application/octet-stream` is in here because it is what the multipart
 * serializer substitutes for a Blob with no type — it is the ABSENCE of a
 * declaration wearing the costume of one, and treating it as a real answer is
 * precisely the defect this module exists to end.
 */
const UNDECLARED_TYPES: ReadonlySet<string> = new Set([
  '',
  'application/octet-stream',
  'binary/octet-stream',
  'application/binary',
  'application/unknown',
]);

/** `image/JPEG; charset=x` → `image/jpeg`. */
export function normalizeContentType(raw: string | undefined | null): string {
  return (raw ?? '').split(';')[0]?.trim().toLowerCase() ?? '';
}

/** True when the value carries no information about what the bytes are. */
export function isUndeclaredContentType(raw: string | undefined | null): boolean {
  return UNDECLARED_TYPES.has(normalizeContentType(raw));
}

/** The canonical accepted type, or null. `image/jpg` normalises to `image/jpeg`. */
export function acceptedPhotoType(raw: string | undefined | null): string | null {
  const clean = normalizeContentType(raw);
  if (!(clean in ACCEPTED_PHOTO_TYPES)) return null;
  return clean === 'image/jpg' ? 'image/jpeg' : clean;
}

/** The R2 key's extension for an accepted type, or null if it is not one. */
export function extensionForPhoto(raw: string | undefined | null): string | null {
  return ACCEPTED_PHOTO_TYPES[normalizeContentType(raw)] ?? null;
}

/** Every accepted type, for a message that has to name them. */
export function acceptedPhotoTypeList(): readonly string[] {
  return Object.keys(ACCEPTED_PHOTO_TYPES);
}

/* ═════════════════════════════ what it really is ═══════════════════════════ */

/** Enough for every signature below; `ftyp` brands live at bytes 8–12. */
export const PHOTO_SNIFF_BYTES = 16;

function starts(head: Uint8Array, offset: number, ascii: string): boolean {
  for (let i = 0; i < ascii.length; i++) {
    if (head[offset + i] !== ascii.charCodeAt(i)) return false;
  }
  return true;
}

/**
 * ISO-BMFF brands. HEIC and AVIF are the same container with a different brand,
 * which is why a file's extension is such a poor witness for either of them.
 */
const FTYP_BRANDS: Readonly<Record<string, string>> = Object.freeze({
  avif: 'image/avif',
  avis: 'image/avif',
  heic: 'image/heic',
  heix: 'image/heic',
  heim: 'image/heic',
  heis: 'image/heic',
  hevc: 'image/heic',
  hevx: 'image/heic',
  hevm: 'image/heic',
  hevs: 'image/heic',
  mif1: 'image/heif',
  msf1: 'image/heif',
});

/**
 * What the first bytes of a file say it is, or null.
 *
 * Only the types the office accepts are recognised: this is not a general
 * format detector, it is the answer to "may I upload this", and a format that
 * would be refused anyway is better reported as "not something the office can
 * open" than as its own name.
 */
export function sniffPhotoType(head: Uint8Array): string | null {
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) {
    return 'image/jpeg';
  }
  if (
    head.length >= 8 &&
    head[0] === 0x89 &&
    starts(head, 1, 'PNG') &&
    head[4] === 0x0d &&
    head[5] === 0x0a &&
    head[6] === 0x1a &&
    head[7] === 0x0a
  ) {
    return 'image/png';
  }
  if (head.length >= 12 && starts(head, 0, 'RIFF') && starts(head, 8, 'WEBP')) {
    return 'image/webp';
  }
  if (head.length >= 12 && starts(head, 4, 'ftyp')) {
    let brand = '';
    for (let i = 8; i < 12; i++) brand += String.fromCharCode(head[i] ?? 0);
    return FTYP_BRANDS[brand.toLowerCase()] ?? null;
  }
  return null;
}

/** `IMG_0042.HEIC` → `image/heic`. The weakest witness, so it is asked last. */
export function photoTypeFromName(name: string | undefined): string | null {
  const dot = (name ?? '').lastIndexOf('.');
  if (dot < 0) return null;
  const ext = (name ?? '').slice(dot + 1).toLowerCase();
  for (const [type, mapped] of Object.entries(ACCEPTED_PHOTO_TYPES)) {
    if (mapped === ext) return acceptedPhotoType(type);
  }
  return ext === 'jpeg' ? 'image/jpeg' : null;
}

/* ═══════════════════════════ the capture decision ══════════════════════════ */

/** Why a file cannot be uploaded, when it cannot. */
export type PhotoRejectionReason = 'empty' | 'too_big' | 'unsupported';

export interface ResolvedPhotoType {
  ok: true;
  /** The canonical accepted type. Never empty, never `application/octet-stream`. */
  contentType: string;
  /** The R2 key's extension for it. */
  extension: string;
  /** How it was decided — for a log line, and for a test that asserts the order. */
  source: 'declared' | 'sniffed' | 'filename';
}

export interface RejectedPhoto {
  ok: false;
  reason: PhotoRejectionReason;
  /** Plain language for the surveyor. Says what happened and what to do. */
  message: string;
  /** The engineering half: what the file claimed to be. For a log. */
  declared: string;
}

export type PhotoTypeDecision = ResolvedPhotoType | RejectedPhoto;

/** A `File` in the browser; a `Blob` plus an optional name everywhere else. */
export interface NamedBlob extends Blob {
  readonly name?: string;
}

/**
 * Work out what this file is, or say — in words a surveyor can act on — why it
 * cannot be sent. Reads at most the first 16 bytes.
 *
 * D-015: every refusal leads with what is true (nothing was lost, the picture is
 * still there) and ends with the one thing to do next.
 */
export async function resolvePhotoType(file: NamedBlob): Promise<PhotoTypeDecision> {
  const declared = normalizeContentType(file.type);

  if (file.size === 0) {
    return {
      ok: false,
      reason: 'empty',
      declared,
      message:
        'That picture came through empty — the phone handed over a file with nothing in it. ' +
        'Nothing was saved. Take it again.',
    };
  }
  if (file.size > MAX_PHOTO_BYTES) {
    return {
      ok: false,
      reason: 'too_big',
      declared,
      message:
        `That picture is too big for the office to accept (${megabytes(file.size)}, ` +
        `and the limit is ${megabytes(MAX_PHOTO_BYTES)}). Nothing was saved. ` +
        'Take it again with the camera, or choose a smaller one.',
    };
  }

  // 1. What the OS said, if the office can open it.
  const fromDeclared = acceptedPhotoType(declared);
  if (fromDeclared) {
    return { ok: true, contentType: fromDeclared, extension: extension(fromDeclared), source: 'declared' };
  }

  // 2. What the bytes say. THIS IS THE LIVE-CAMERA FIX: an iOS capture that
  //    arrives with no type at all still begins `FF D8 FF`, and that is not an
  //    opinion.
  const head = new Uint8Array(await file.slice(0, PHOTO_SNIFF_BYTES).arrayBuffer());
  const sniffed = acceptedPhotoType(sniffPhotoType(head));
  if (sniffed) {
    return { ok: true, contentType: sniffed, extension: extension(sniffed), source: 'sniffed' };
  }

  // 3. The name, last and least.
  //    Only consulted when nothing declared a type: a file that DID declare one
  //    the office cannot open (a TIFF, a PDF) must not be rescued by having been
  //    named `.jpg`, or the office receives bytes it cannot display.
  if (isUndeclaredContentType(declared)) {
    const named = acceptedPhotoType(photoTypeFromName(file.name));
    if (named) {
      return { ok: true, contentType: named, extension: extension(named), source: 'filename' };
    }
  }

  return {
    ok: false,
    reason: 'unsupported',
    declared,
    message: declared
      ? `The office cannot open this kind of picture file (${declared}). Nothing was saved. ` +
        'Take a photo with the camera instead, or choose a different picture.'
      : 'The office cannot open this kind of file — it does not look like a photo. ' +
        'Nothing was saved. Take a photo with the camera instead.',
  };
}

/**
 * The bytes of a picture, READ — never a view of them.
 *
 * This replaces `typedPhotoBlob`, whose `Blob.slice()` view is what an iPhone
 * serialised as an empty upload for twelve days (module header). A
 * `Uint8Array` is bytes in the page's own memory: it is what goes into
 * IndexedDB at capture (wrapped in a Blob built from it, so the stored record
 * is a copy of the picture and not a reference to the camera's temp file) and
 * what goes over the wire at upload, as the request body itself.
 */
export async function readPhotoBytes(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/** A Blob built FROM bytes, typed — the shape the store keeps and thumbnails render. */
export function photoBlobFromBytes(bytes: Uint8Array, contentType: string): Blob {
  // A fresh, plain-ArrayBuffer-backed copy: a view over a SharedArrayBuffer or a
  // subarray is not a BlobPart everywhere, and a copy is the point anyway.
  return new Blob([new Uint8Array(bytes)], { type: normalizeContentType(contentType) || contentType });
}

/* ═════════════════ the raw upload path's metadata header ═══════════════════ */

/**
 * The header the device sends the photo's metadata in when the body is the raw
 * picture. Base64 of the UTF-8 JSON, because a header value is ASCII and a room
 * name is not. One encoder and one decoder, in the module both ends import.
 */
export const PHOTO_META_HEADER = 'x-photo-meta';

export function encodePhotoMetaHeader(meta: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(meta));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** The inverse of `encodePhotoMetaHeader`. Throws on anything it cannot read. */
export function decodePhotoMetaHeader(value: string): unknown {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return JSON.parse(new TextDecoder().decode(bytes)) as unknown;
}

/**
 * The filename for the multipart part.
 *
 * A part with an EMPTY filename is not parsed as a file at all — `FormData.get`
 * hands back a string and the Worker's `file instanceof File` check refuses it
 * as "missing photo part". The photo id is never empty, so this cannot happen;
 * the extension is added because some intermediaries (and every human reading a
 * tail) go by the name.
 */
export function photoPartFilename(photoId: string, contentType: string): string {
  const id = photoId.trim() || 'photo';
  return `${id}.${extensionForPhoto(contentType) ?? 'jpg'}`;
}

function extension(contentType: string): string {
  return extensionForPhoto(contentType) ?? 'jpg';
}

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}
